import isArray from 'lodash/isArray';
import produce from 'immer';

import * as K from 'common/constants';
import { LogEntryData, LogEntryEdge, QueryType, SortOrder } from 'common/graphqlTypes';
import { padLogIdList, updateCursors, updateLogIdList } from 'common/producers';
import { EventCacheData, PaginatedData } from 'hooks/useLRQ/useEventCache';
import { LRQRequest } from 'hooks/useLRQ/useLRQ.types';
import { ErrorResult } from 'hooks/useLRQ/useLRQ';

/**
 * Returns the request IDs for any requests of the given type(s). If no type is provided, returns all IDs. The requests
 * that have the IDs are the grouped queries within the larger LRQ request.
 *
 * An empty LRQ request (undefined or null or empty string) within an array will return the empty string as its ID.
 *
 * @param request    an LRQRequest, or a list of them
 * @param type       a query type, or a list of them
 *
 * @returns list of request IDs
 */
export const getRequestIds = (
  request?: LRQRequest | Array<LRQRequest | string | undefined> | null,
  type?: QueryType | QueryType[],
): string[] => {
  if (request == null) {
    return [];
  }

  const requests = isArray(request) ? (request as LRQRequest[]) : [request];
  const types = isArray(type) ? type : [type];
  const queryIds: string[] = [];
  for (let r = 0; r < requests.length; r++) {
    const req = requests[r];
    if (!req || !req.queriesVariables || req.queriesVariables.length === 0) {
      queryIds.push('');
    } else {
      for (let q = 0; q < req.queriesVariables.length; q++) {
        const query = req.queriesVariables[q];
        // for any ID to be included for a query, it has to match the given type (if one was provided)
        if (!type || types.includes(query.type)) {
          // and it can't be the same as one we already have
          if (!query?.id || !queryIds.includes(query.id)) {
            // if the ID is somehow falsy, add empty string
            queryIds.push(query?.id || '');
          }
        }
      }
    }
  }

  return queryIds;
};

/**
 * Returns the first request ID found for any requests of the given type(s). If no type is provided, returns the first ID.
 *
 * @param request    an LRQRequest, or a list of them
 * @param type       a query type, or a list of them
 *
 * @returns a single ID, or empty string if none found
 */
export const getRequestId = (
  request?: LRQRequest | Array<LRQRequest | undefined> | null,
  type?: QueryType | QueryType[],
): string => {
  const ids = getRequestIds(request, type);
  return ids.length > 0 ? ids[0] : '';
};

export const DEFAULT_PAGINATED_DATA: PaginatedData = {
  currentIds: [],
  currentLogEntryEdges: [],
  cursors: [],
  includesFirstLog: false,
  includesLastLog: false,
  previousIds: [],
};
/**
 * Takes the results of one or more paginated log requests and reduces them to a single object. Most notably, this
 * function combines the log event matches from individual {@see LogEntryData} objects together into a single ordered
 * list with no duplicates and placeholders for yet unloaded log events.
 *
 * @param results            result data from the event cache for each paginated request, which is to be reduced
 * @param matchCount         most reliable available matchCount for the active search context
 * @param getLogEntryEdge    function to get a LogEntryEdge from a continuationToken
 * @param sortOrder          the sort order that will be used to display the events
 *
 * @returns PaginatedData
 */
export const getPaginatedData = (
  results: Array<EventCacheData>,
  matchCount: number,
  getLogEntryEdge?: (continuationToken: string) => LogEntryEdge | null,
  sortOrder = K.DEFAULT_EVENT_SORT,
): PaginatedData => {
  const paginatedData = results.reduce(
    (
      { currentIds, currentLogEntryEdges, cursors, includesFirstLog, includesLastLog, previousIds }: PaginatedData,
      result: EventCacheData,
      index: number,
    ): PaginatedData => {
      const { isDone, logEntryData, queryVariables } = result || {};
      const { matches } = logEntryData || {};
      const { ascending, numResults } = queryVariables || {};

      /**
       * `includesFirstLog` and `includesLastLog` track whether the first (top of screen) and last (bottom of screen)
       * logs in the result set are included as we reduce the results.
       *
       * We need to check for two possibilities:
       *   1. We meet the first condition when the user starts at one end of the results and is only able to scroll in
       *      one direction. In this case `isAtBoundary` won't apply.
       *   2. On the other hand, if we do hit the `isAtBoundary` condition while scrolling away from the position we
       *      started in, it may be that there are empty indices in the paginated requests array. This can happen when we
       *      know the total `matchCount` for a centered search, but we don't know the count in each direction.
       */
      const isFirstIndex = index === 0;
      const isLastIndex = index === results.length - 1;
      const isAtBoundary = isDone && matches?.length < numResults;
      const isScrollingUp =
        (ascending && sortOrder === SortOrder.MOST_RECENT_AT_TOP) ||
        (!ascending && sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM);
      const foundTopOfResultSet = (!!matches && isFirstIndex) || (isAtBoundary && isScrollingUp);
      const foundBottomOfResultSet = (!!matches && isLastIndex) || (isAtBoundary && !isScrollingUp);
      const updatedIncludesFirstLog = foundTopOfResultSet || includesFirstLog;
      const updatedIncludesLastLog = foundBottomOfResultSet || includesLastLog;

      // early return if matches is falsy, which indicates either that the results are not yet in the cache for the
      // request or that the request does not include logEntryData
      if (!matches) {
        return {
          currentIds,
          currentLogEntryEdges,
          cursors,
          includesFirstLog: updatedIncludesFirstLog,
          includesLastLog: updatedIncludesLastLog,
          previousIds,
        };
      }

      // set the cursor for the next paginated request
      const hasMoreData = matches.length === numResults;
      const cursorReady = isDone && hasMoreData;
      const [cursorEdge] = ascending ? matches.slice(-1) : matches.slice(0, 1);
      const cursor = (cursorReady && cursorEdge) || null;
      const mergedCursors = produce(cursors, updateCursors(cursor, index));

      // update the list of continuationToken ids representing the data set
      const newIds = matches.map((match: LogEntryEdge) => match.continuationToken);
      // reverse before merging, if needed
      if (sortOrder === SortOrder.MOST_RECENT_AT_TOP) {
        newIds.reverse();
      }
      const mergedIds = produce(previousIds, updateLogIdList(newIds));

      return {
        currentIds, // calculated outside the loop for performance
        currentLogEntryEdges, // calculated outside the loop for performance
        cursors: mergedCursors,
        includesFirstLog: updatedIncludesFirstLog,
        includesLastLog: updatedIncludesLastLog,
        previousIds: mergedIds,
      };
    },
    { ...DEFAULT_PAGINATED_DATA },
  );

  // pad the results up to represent unloaded lines, and map the ids back to their edges.
  paginatedData.currentIds = produce(
    paginatedData.previousIds,
    padLogIdList(results.length, paginatedData.includesFirstLog, paginatedData.includesLastLog, matchCount),
  );

  paginatedData.currentLogEntryEdges = paginatedData.currentIds.map(
    (id, indx) =>
      (getLogEntryEdge && getLogEntryEdge(id)) || { notLoaded: true, continuationToken: String(indx), logEntry: null },
  );
  return paginatedData;
};

/**
 * This custom cache lives in module scope. This is an unusual and anti-modular solution, but useEventCache has unusual
 * and costly performance problems. As a data hook, useEventCache is called many times by different components that are
 * looking for the same data. Thus the solution provided here is to share a single cache across the app by using module
 * scope, and to limit the size of the cache to one member. There is currently no valid use case where two components
 * look at different paginatedData instances at the same time, so this is the simplest way to avoid introducing a new
 * memory leak with this cache.
 **/
let paginatedDataCache = {
  keyList: [] as Array<number | SortOrder | LogEntryData>,
  value: { ...DEFAULT_PAGINATED_DATA },
};

/**
 * Implements a simple caching layer on top of {@see getPaginatedData} for performance. Note that this function depends
 * on a module-scoped cache, and is therefore not pure.
 *
 * @param results               result data from the event cache for each paginated request
 * @param matchCount            most reliable available matchCount for the active search context
 * @param getLogEntryEdge       function to get a LogEntryEdge from a continuationToken
 * @param sortOrder             the sort order that will be used to display the events
 *
 * @returns PaginatedData
 */
export const getCachedPaginatedData = (
  results: Array<EventCacheData>,
  matchCount: number,
  getLogEntryEdge?: (continuationToken: string) => LogEntryEdge | null,
  sortOrder = K.DEFAULT_EVENT_SORT,
): PaginatedData => {
  // Make a list of the keys we need to check. All of these keys matching the cache indicates a cache hit
  const keyList = ([matchCount, sortOrder] as Array<number | SortOrder | LogEntryData>).concat(
    results.map(({ data }) => data),
  );
  const cacheHit =
    keyList.length === paginatedDataCache.keyList.length &&
    keyList.every((key, index) => key === paginatedDataCache.keyList[index]);
  if (!cacheHit) {
    // if the keys do not match the cache, then update the cache
    paginatedDataCache = { keyList, value: getPaginatedData(results, matchCount, getLogEntryEdge, sortOrder) };
  }
  // return the cached value
  return paginatedDataCache.value;
};

/**
 * Given a list of error results, return a user-facing error message. We take the first error we find (likely to be from
 * the LOG query), rather than concatenating all the errors into a single message. For syntax errors, the server tends to
 * return something in the form:
 *
 *     field=[filter] error=[...]
 *
 * We pull out the error payload there, which has the relevant error text.
 *
 * @param errors    list of LRQ error results
 * @returns user-readable error message
 */
export const getLrqErrorMessage = (errors: ErrorResult[]) => {
  let message = 'Error';
  const error = errors.find((err) => !!err?.terminationReasons); // take first non-empty error
  message = error?.terminationReasons?.[0] || message; // take first error message
  const errStart = 'error=[';
  const errorMsgIdx = message.indexOf(errStart);
  if (errorMsgIdx !== -1) {
    message = message.substring(errorMsgIdx + errStart.length, message?.lastIndexOf(']'));
  }
  return message;
};
